import React from 'react';
import { Button } from '../components/Button';
import { Input } from '../components/Input';
import { Select } from '../components/Select';
import { Text } from '../components/Text';
import {
  MAKES,
  MODELS_BY_MAKE,
  LOCATIONS,
  COLORS,
  TRANSMISSIONS,
  CONDITIONS,
  FUEL_TYPES,
} from '../data/FilterOptions';
import { BODY_TYPES } from '../data/listingOptions';

const CURRENT_YEAR = new Date().getFullYear();
const YEARS = Array.from({ length: 12 }, (_, i) => String(CURRENT_YEAR - i));

// Normalizes plain-string options and {value,label} options into one shape,
// so the select renderer below never branches on option type.
const normalizeOptions = (options) =>
  options.map((opt) => (typeof opt === 'string' ? { value: opt, label: opt } : opt));

const SelectFilter = ({ label, value, options, placeholder, disabled, onChange }) => (
  <div className="mb-6">
    <h4 className="font-bold text-text mb-3">{label}</h4>
    <Select
      className="w-full px-3 py-2 bg-white rounded-lg border border-border text-sm outline-none focus:border-primary cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
    >
      <option value="">{placeholder}</option>
      {normalizeOptions(options).map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </Select>
  </div>
);

const CheckboxGroupFilter = ({ label, options, selected, onToggle }) => (
  <div className="mb-6">
    <h4 className="font-bold text-text mb-3">{label}</h4>
    <div className="space-y-2">
      {options.map((option) => (
        <label key={option} className="flex items-center gap-2 cursor-pointer">
          <Input
            variant="text"
            type="checkbox"
            className="w-4 h-4 text-primary rounded border-border focus:ring-primary"
            checked={selected.includes(option)}
            onChange={() => onToggle(option)}
          />
          <Text variant="muted" className="text-muted">{option}</Text>
        </label>
      ))}
    </div>
  </div>
);

const FilterSidebar = ({ filters, setFilter, toggleArrayFilter, resetFilters }) => {
  const modelOptions = filters.make ? MODELS_BY_MAKE[filters.make] ?? [] : [];

  const selectFilters = [
    { key: 'make', label: 'Make', options: MAKES, placeholder: 'All Makes' },
    {
      key: 'model',
      label: 'Model',
      options: modelOptions,
      placeholder: filters.make ? 'All Models' : 'Select a make first',
      disabled: !filters.make,
    },
    { key: 'location', label: 'Location', options: LOCATIONS, placeholder: 'All Locations' },
    { key: 'color', label: 'Color', options: COLORS, placeholder: 'All Colors' },
    { key: 'transmission', label: 'Transmission', options: TRANSMISSIONS, placeholder: 'Any Transmission' },
    { key: 'condition', label: 'Condition', options: CONDITIONS, placeholder: 'Any Condition' },
    { key: 'yearMin', label: 'Year', options: YEARS, placeholder: 'All Years' },
  ];

  const checkboxFilters = [
    { key: 'bodyType', label: 'Category', options: BODY_TYPES },
    { key: 'fuelType', label: 'Fuel Type', options: FUEL_TYPES },
  ];

  return (
    <div className="glass-card rounded-2xl p-6 sticky top-28">
      <h3 className="text-xl font-heading font-bold text-dark mb-6">Filters</h3>

      {checkboxFilters.map((group) => (
        <CheckboxGroupFilter
          key={group.key}
          label={group.label}
          options={group.options}
          selected={filters[group.key]}
          onToggle={(value) => toggleArrayFilter(group.key, value)}
        />
      ))}

      <div className="mb-6">
        <h4 className="font-bold text-text mb-3">Price Range (Ksh)</h4>
        <div className="flex gap-2">
          <Input
            variant="text"
            type="number"
            placeholder="Min"
            className="w-full px-3 py-2 bg-white rounded-lg border border-border text-sm outline-none focus:border-primary"
            value={filters.priceMin}
            onChange={(e) => setFilter('priceMin', e.target.value)}
          />
          <Input
            variant="text"
            type="number"
            placeholder="Max"
            className="w-full px-3 py-2 bg-white rounded-lg border border-border text-sm outline-none focus:border-primary"
            value={filters.priceMax}
            onChange={(e) => setFilter('priceMax', e.target.value)}
          />
        </div>
      </div>

      {selectFilters.map((sel) => (
        <SelectFilter
          key={sel.key}
          label={sel.label}
          value={filters[sel.key]}
          options={sel.options}
          placeholder={sel.placeholder}
          disabled={sel.disabled}
          onChange={(value) => setFilter(sel.key, value)}
        />
      ))}

      <Button
        variant="primary"
        className="w-full px-6 py-3 bg-white border border-border hover:bg-gray-100 text-dark font-bold rounded-xl transition-colors cursor-pointer"
        onClick={resetFilters}
      >
        Clear Filters
      </Button>
    </div>
  );
};

export default FilterSidebar;